<template>
  <div>
    <div class="home-page">
      <v-row class="mt-1">

        <v-col v-if="showMarketStatus" lg=12 sm=12 md=12 cols=12>
          <v-chip
            large
            class="d-block white--text v-chip-right-close"
            :color="marketStatus ? 'success' : 'danger'"
            close
            @click:close="closeMarketStatusChip"
          >
            <div>
              <v-icon v-if="marketStatus" left size="26" color="rgba(255,255,255, 0.6)" class="ml-0 mr-2">mdi-clock-outline</v-icon>
              <v-icon v-else left size="26" color="rgba(255,255,255, 0.5)" class="ml-0 mr-2">mdi-lock</v-icon>
              {{ this.marketStatus ? $t('home.market.open') : $t('home.market.close') }}
            </div>
          </v-chip>
        </v-col>

        <v-col lg=3 sm=6 md=6 cols=12>
          <v-card class="card-style px-1" outlined elevation="2" height="240">
            <v-card-title class="d-flex flex-nowrap pa-0 justify-center">
              <p class="text-h5 text-truncate mb-3">{{ $t('home.todaySignal.title') }}</p>
            </v-card-title>
            <v-card-text class="px-5 my-1">
              <v-skeleton-loader
                v-if="loadingSignalSummary"
                type="image"
                max-height="137"
              ></v-skeleton-loader>
              <v-row v-else no-gutters class="pb-2" justify="space-between">
                <v-col cols="5" class="my-auto pt-1">
                  <v-row class="pl-4 mb-5">
                    <span style="font-size: 20px;">{{ $t('home.todaySignal.breakouts') }}: <span class="greyBold--text font-weight-medium">{{  signalSummary.lastSession.breakout  }}</span></span>
                  </v-row>
                  <v-row class="pl-4">
                    <v-col cols="6" class="my-auto pl-0 pt-0">
                      <span class="font-weight-medium success-text" style="font-size: 20px;">{{  signalSummary.lastSession.above  }}</span>
                      <p class="greyBold--text mb-0" style="font-size: 11px;">{{ $t('home.todaySignal.above') }}</p>
                    </v-col>
                    <v-col cols="6" class="my-auto pt-0">
                      <span class="font-weight-medium danger-text" style="font-size: 20px;">{{  signalSummary.lastSession.below  }}</span>
                      <p class="greyBold--text mb-0" style="font-size: 11px;">{{ $t('home.todaySignal.below') }}</p>
                    </v-col>
                  </v-row>
                  <v-row class="pl-4">
                    <v-btn color="primaryConst" block outlined class="button-shadow" @click="pushLastSessionActiveSignalsPage()">{{ $t('home.detailUpperCase') }}</v-btn>
                  </v-row>
                </v-col>
                <v-col cols="7">
                  <ApexChart v-if="apexLoading" height="180" :options="mock.apexPie1.options" :series="[getLastSessionPercentage()]"></ApexChart>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col lg=3 sm=6 md=6 cols=12>
          <v-card class="card-style px-1" outlined elevation="2" height="240">
            <v-card-title class="d-flex flex-nowrap pa-0 justify-center">
              <p class="text-h5 text-truncate mb-3">{{ $t('home.potentialSignal.title') }}</p>
            </v-card-title>
            <v-card-text class="px-5 mt-2 mb-1">
              <v-skeleton-loader
                v-if="loadingSignalSummary"
                type="image"
                max-height="137"
              ></v-skeleton-loader>
              <v-row v-else no-gutters class="pb-2" justify="space-between">
                <v-col cols="5" class="my-auto pt-1">
                  <v-row class="pl-4 mb-2">
                    <span style="font-size: 16px;">{{ $t('home.potentialSignal.high') }}: <span class="greyBold--text font-weight-medium">{{ this.signalSummary.potentialsSummary.potential }}</span></span>
                  </v-row>
                  <v-row class="pl-4 mb-2">
                    <span style="font-size: 16px;">{{ $t('home.potentialSignal.likey') }}: <span class="greyBold--text font-weight-medium">{{ this.signalSummary.potentialsSummary.likely }}</span></span>
                  </v-row>
                  <v-row class="pl-4 mb-5">
                    <span style="font-size: 16px;">{{ $t('home.potentialSignal.eventual') }}: <span class="greyBold--text font-weight-medium">{{ this.signalSummary.potentialsSummary.eventual }}</span></span>
                  </v-row>
                  <v-row class="pl-4">
                    <v-btn color="primaryConst" block outlined class="button-shadow" @click="pushPotentialSignalsPage()">{{ $t('home.detailUpperCase') }}</v-btn>
                  </v-row>
                </v-col>
                <v-col cols="7">
                  <ApexChart
                    v-if="apexLoading"
                    height="165"
                    type="donut"
                    :options="mock.apexPie.options"
                    :series="getTotalForPotentialSignalSummary()">
                  </ApexChart>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col lg=3 sm=6 md=6 cols=12>
          <v-card class="card-style px-1" outlined elevation="2" height="240">
            <v-card-title class="d-flex flex-nowrap pa-0 justify-center">
              <p class="text-h5 text-truncate mb-3">{{ $t('home.mainIndices.title') }}</p>
            </v-card-title>
            <v-card-text class="pa-5 pt-0">
              <v-row class="mb-2" no-gutters>
                <v-col cols="5" class="mt-auto">
                  <span class="greyBold--text" style="font-size: 17px;"><i>{{ $t('home.mainIndices.dow') }}</i></span>
                </v-col>
                <v-col cols="7">
                  <ApexChart v-if="apexLoading" ref="apexArea1" height="43" type="area" :options="mock.apexArea1.options" :series="mock.apexArea1.series"></ApexChart>
                </v-col>
              </v-row>
              <v-row class="mb-2" no-gutters>
                <v-col cols="5" class="mt-auto">
                  <span class="greyBold--text"  style="font-size: 17px;"><i>{{ $t('home.mainIndices.nasdaq') }}</i></span>
                </v-col >
                <v-col cols="7">
                  <ApexChart v-if="apexLoading" ref="apexArea2" height="43" type="area" :options="mock.apexArea2.options" :series="mock.apexArea2.series"></ApexChart>
                </v-col>
              </v-row>
              <v-row class="mb-1" no-gutters>
                <v-col cols="5" class="mt-auto">
                  <span class="greyBold--text"  style="font-size: 17px;"><i>{{ $t('home.mainIndices.sp500') }}</i></span>
                </v-col>
                <v-col cols="7">
                  <ApexChart v-if="apexLoading" ref="apexArea3" height="43" type="area" :options="mock.apexArea3.options" :series="mock.apexArea3.series"></ApexChart>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col lg=3 sm=6 md=6 cols=12>
          <v-card class="card-style px-1" outlined elevation="2" height="240">
            <v-card-title class="d-flex flex-nowrap pa-0 justify-center">
              <p class="text-h5 text-truncate mb-3">{{ $t('home.marketSentiment.title') }}</p>
            </v-card-title>
            <v-card-text class="pa-5 pt-1">
              <div v-if="!loadingMarketSentiment">
                <v-row no-gutters class="pb-4">
                  <v-col>
                    <div class="greyMedium--text fs-large mb-1">{{ $t('home.marketSentiment.today') }}</div>
                    <v-progress-linear
                      :value=marketSentiment.today
                      background-color="#ececec"
                      :color="getSentimentColor(marketSentiment.today)"
                    ></v-progress-linear>
                  </v-col>
                </v-row>
                <v-row no-gutters class="pb-4">
                  <v-col>
                    <div class="greyMedium--text fs-large mb-1">{{ $t('home.marketSentiment.week') }}</div>
                    <v-progress-linear
                      :value=marketSentiment.week
                      background-color="#ececec"
                      :color="getSentimentColor(marketSentiment.week)"
                    ></v-progress-linear>
                  </v-col>
                </v-row>
                <v-row no-gutters class="pb-4">
                  <v-col>
                    <div class="greyMedium--text fs-large mb-1">{{ $t('home.marketSentiment.month') }}</div>
                    <v-progress-linear
                      :value=marketSentiment.month
                      background-color="#ececec"
                      :color="getSentimentColor(marketSentiment.month)"
                    ></v-progress-linear>
                  </v-col>
                </v-row>
              </div>
              <v-skeleton-loader
                v-if="loadingMarketSentiment"
                type="image"
                max-height="137"
              ></v-skeleton-loader> <!-- type = table-tbody -->
            </v-card-text>
          </v-card>
        </v-col>

        <v-col lg=4 sm=12 md=12 cols=12>
          <v-card class="card-style px-0" outlined elevation="2" height="400">
            <v-card-title class="d-flex flex-nowrap pa-0 justify-center">
              <p class="text-h5 text-truncate mb-3">{{ $t('home.recentSignals.title') }}</p>
            </v-card-title>
            <v-card-text class="px-3 mt-2 mb-1">
              <v-skeleton-loader
                v-if="loadingSignalSummary"
                type="table-tbody"
                max-height="297"
              ></v-skeleton-loader>
              <v-data-table 
                  v-else
                  class="material-table elevation-1"
                  mobile-breakpoint="0"
                  :show-expand="showTableRowButtonDetail"
                  :headers="recentSignalHeaders" 
                  :items="signalSummary.recentSignals" 
                  :hide-default-footer="true"
                  :height="300"
                  :expanded.sync="recentSignalExpanded"
                  :no-data-text="$t('home.recentSignals.table.empty')"
                  @click:row="toggleExpandTable(recentSignalExpanded, $event)"
                >
                <template v-slot:item.ticker="{ item }">
                  <a @click="pushSignalDetailPage(item.id)">{{ item.ticker }}</a>
                </template>
                <template v-slot:item.rvol="{ item }">
                  <div class="same-line-flex justify-center">
                    <span class="min-width-50">{{ formatNumber(item.rVol, true) }} ×</span>
                  </div>
                </template>
                <template v-slot:item.status="{ item }">
                  <div v-if="item.price >= item.signalPrice" class="text-center text-no-wrap">
                    {{calculateGrowthPercentageFormatted(item.price, item.signalPrice)}}% <v-icon color="success">mdi-arrow-top-right</v-icon>
                  </div>
                  <div v-else class="text-center text-no-wrap">
                    {{calculateGrowthPercentageFormatted(item.price, item.signalPrice)}}% <v-icon color="error">mdi-arrow-bottom-right</v-icon>
                  </div>
                </template>
                <template v-slot:expanded-item="{ headers, item }">
                  <td :colspan="headers.length">
                    <div class="pt-3 pb-2">
                      <v-row align="center" justify="center">
                        <v-col cols="5" class="text-center pt-4">
                          <span class="font-weight-medium">{{ $t('home.recentSignals.sector') }}</span>
                        </v-col>
                        <v-col cols="7" class="text-center pt-4">
                          <span class="font-weight-regular">{{ item.sector }}</span>
                        </v-col>
                      </v-row>
                      <v-divider class="ma-2"></v-divider>
                      <v-row align="center" justify="center">
                        <v-col cols="5" class="text-center">
                          <span class="font-weight-medium">{{ $t('home.recentSignals.signalDate') }}</span>
                        </v-col>
                        <v-col cols="7" class="text-center">
                          <span class="greyBold--text font-weight-regular">{{ formatDate(item.signalDate) }}</span>
                        </v-col>
                      </v-row>
                      <v-divider class="ma-2"></v-divider>
                      <v-row align="center" justify="center">
                        <v-col cols="5" class="text-center">
                          <span class="font-weight-medium">{{ $t('home.recentSignals.signalPrice') }}</span>
                        </v-col>
                        <v-col cols="7" class="text-center">
                          <span class="greyBold--text font-weight-regular">$ {{ formatNumber(item.signalPrice, false) }}</span>
                        </v-col>
                      </v-row>
                      <v-divider class="ma-2"></v-divider>
                      <v-row align="center" justify="center">
                        <v-col cols="5" class="text-center">
                          <span class="font-weight-medium">{{ $t('home.recentSignals.currentPrice') }}</span>
                        </v-col>
                        <v-col cols="7" class="text-center">
                          <span class="greyBold--text font-weight-regular">$ {{ formatNumber(item.price, false) }}</span>
                        </v-col>
                      </v-row>
                      <v-divider class="ma-2"></v-divider>
                      <v-row align="center" justify="center">
                        <v-col cols="5" class="text-center">
                          <span class="font-weight-medium">{{ $t('home.recentSignals.technical') }}</span>
                        </v-col>
                        <v-col cols="7" class="text-center">
                          <div>
                            <v-chip
                              link
                              :color="getCustomIndicatorsColor(item.indicators.technical)"
                            >
                              {{item.indicators.technical}}%
                            </v-chip>
                          </div>
                        </v-col>
                      </v-row>
                      <v-divider class="ma-2"></v-divider>
                      <v-row align="center" justify="center">
                        <v-col cols="5" class="text-center">
                          <span class="font-weight-medium">{{ $t('home.recentSignals.fundamental') }}</span>
                        </v-col>
                        <v-col cols="7" class="text-center">
                          <div>
                            <v-chip
                              link
                              :color="getCustomIndicatorsColor(item.indicators.fundamental)"
                            >
                              {{item.indicators.fundamental}}%
                            </v-chip>
                          </div>
                        </v-col>
                      </v-row>
                      <v-divider class="ma-2"></v-divider>
                      <v-row align="center" justify="center" class="pb-1">
                        <v-col cols="5" class="text-center">
                          <span class="font-weight-medium">{{ $t('home.recentSignals.strength') }}</span>
                        </v-col>
                        <v-col cols="7" class="text-center">
                          <div>
                            <v-chip
                              link
                              :color="getCustomIndicatorsColor(item.indicators.strength)"
                            >
                              {{item.indicators.strength}}%
                            </v-chip>
                          </div>
                        </v-col>
                      </v-row>
                    </div>
                  </td>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col lg=4 sm=12 md=12 cols=12>
          <v-card class="card-style px-0" outlined elevation="2" height="400">
            <v-card-title class="d-flex flex-nowrap pa-0 justify-center">
              <p class="text-h5 text-truncate mb-3">{{ $t('home.potentialSignalSummary.title') }}</p>
            </v-card-title>
            <v-card-text class="px-3 mt-2 mb-1">
              <v-skeleton-loader
                v-if="loadingSignalSummary"
                type="table-tbody"
                max-height="297"
              ></v-skeleton-loader>
              <v-data-table 
                  v-else
                  class="material-table elevation-1"
                  mobile-breakpoint="0"
                  :show-expand="showTableRowButtonDetail"
                  :headers="potentialSignalHeaders" 
                  :items="signalSummary.potentialSignals" 
                  :hide-default-footer="true"
                  :height="300"
                  :expanded.sync="potentialSignalExpanded"
                  @click:row="toggleExpandTable(potentialSignalExpanded, $event)"
                >
                <template v-slot:item.ticker="{ item }">
                  <a @click="pushSignalDetailPage(item.id)">{{ item.ticker }}</a>
                </template>
                <template v-slot:item.price="{ item }">
                  <span>$ {{ formatNumber(item.price, false) }}</span>
                </template>
                <template v-slot:item.signalPrice="{ item }">
                  <span>$ {{ formatNumber(item.signalPrice, false) }}</span>
                </template>
                <template v-slot:expanded-item="{ headers, item }">
                  <td :colspan="headers.length">
                    <div class="pt-3 pb-2">
                      <v-row align="center" justify="center">
                        <v-col cols="5" class="text-center pt-4">
                          <span class="font-weight-medium">{{ $t('home.potentialSignalSummary.sector') }}</span>
                        </v-col>
                        <v-col cols="7" class="text-center pt-4">
                          <span class="font-weight-regular">{{ item.sector }}</span>
                        </v-col>
                      </v-row>
                      <v-divider class="ma-2"></v-divider>
                      <v-row align="center" justify="center">
                        <v-col cols="5" class="text-center">
                          <span class="font-weight-medium">{{ $t('home.potentialSignalSummary.currentVolume') }}</span>
                        </v-col>
                        <v-col cols="7" class="text-center">
                          <span class="greyBold--text font-weight-regular">{{ formatNumber(item.volume, false) }}</span>
                        </v-col>
                      </v-row>
                      <v-divider class="ma-2"></v-divider>
                      <v-row align="center" justify="center">
                        <v-col cols="5" class="text-center">
                          <span class="font-weight-medium">{{ $t('home.potentialSignalSummary.signalVolume') }}</span>
                        </v-col>
                        <v-col cols="7" class="text-center">
                          <span class="greyBold--text font-weight-regular">{{ formatNumber(item.signalVolume, false) }}</span>
                        </v-col>
                      </v-row>
                      <v-divider class="ma-2"></v-divider>
                      <v-row align="center" justify="center">
                        <v-col cols="5" class="text-center">
                          <span class="font-weight-medium">{{ $t('home.potentialSignalSummary.technical') }}</span>
                        </v-col>
                        <v-col cols="7" class="text-center">
                          <div>
                            <v-chip
                              link
                              :color="getCustomIndicatorsColor(item.indicators.technical)"
                            >
                              {{item.indicators.technical}}%
                            </v-chip>
                          </div>
                        </v-col>
                      </v-row>
                      <v-divider class="ma-2"></v-divider>
                      <v-row align="center" justify="center">
                        <v-col cols="5" class="text-center">
                          <span class="font-weight-medium">{{ $t('home.potentialSignalSummary.fundamental') }}</span>
                        </v-col>
                        <v-col cols="7" class="text-center">
                          <div>
                            <v-chip
                              link
                              :color="getCustomIndicatorsColor(item.indicators.fundamental)"
                            >
                              {{item.indicators.fundamental}}%
                            </v-chip>
                          </div>
                        </v-col>
                      </v-row>
                      <v-divider class="ma-2"></v-divider>
                      <v-row align="center" justify="center" class="pb-1">
                        <v-col cols="5" class="text-center">
                          <span class="font-weight-medium">{{ $t('home.potentialSignalSummary.strength') }}</span>
                        </v-col>
                        <v-col cols="7" class="text-center">
                          <div>
                            <v-chip
                              link
                              :color="getCustomIndicatorsColor(item.indicators.strength)"
                            >
                              {{item.indicators.strength}}%
                            </v-chip>
                          </div>
                        </v-col>
                      </v-row>
                    </div>
                  </td>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col lg=4 sm=12 md=12 cols=12>
          <v-card class="card-style px-0" outlined elevation="2" height="400">
            <v-card-title class="d-flex flex-nowrap pa-0 justify-center">
              <p class="text-h5 text-truncate mb-3">{{ $t('home.trackRecord.title') }}</p>
            </v-card-title>
            <v-card-text class="px-3 mt-2 mb-1">
              <v-skeleton-loader
                v-if="loadingSignalSummary"
                type="table-tbody"
                max-height="297"
              ></v-skeleton-loader>
              <v-data-table 
                  v-else
                  class="material-table elevation-1"
                  mobile-breakpoint="0"
                  :show-expand="showTableRowButtonDetail"
                  :headers="trackRecordHeaders" 
                  :items="signalSummary.recentTrackRecord" 
                  :hide-default-footer="true"
                  :height="300"
                  :expanded.sync="trackRecordExpanded"
                  @click:row="toggleExpandTable(trackRecordExpanded, $event)"
                >
                <template v-slot:item.ticker="{ item }">
                  <a @click="pushSignalDetailPage(item.id)">{{ item.ticker }}</a>
                </template>
                <template v-slot:item.signalDate="{ item }">
                  <span>{{ formatDate(item.signalDate) }}</span>
                </template>
                <template v-slot:item.gain="{ item }">
                  <td v-if="item.price >= item.signalPrice" class="text-center text-no-wrap">
                    {{calculateGrowthPercentageFormatted(item.price, item.signalPrice)}}% <v-icon color="success">mdi-arrow-top-right</v-icon>
                  </td>
                  <td v-else class="text-center text-no-wrap">
                    {{calculateGrowthPercentageFormatted(item.price, item.signalPrice)}}% <v-icon color="error">mdi-arrow-bottom-right</v-icon>
                  </td>
                </template>
                <template v-slot:expanded-item="{ headers, item }">
                  <td :colspan="headers.length">
                    <div class="pt-3 pb-2">
                      <v-row align="center" justify="center">
                        <v-col cols="5" class="text-center pt-4">
                          <span class="font-weight-medium">{{ $t('home.trackRecord.sector') }}</span>
                        </v-col>
                        <v-col cols="7" class="text-center pt-4">
                          <span class="font-weight-regular">{{ item.sector }}</span>
                        </v-col>
                      </v-row>
                      <v-divider class="ma-2"></v-divider>
                      <v-row align="center" justify="center">
                        <v-col cols="5" class="text-center">
                          <span class="font-weight-medium">{{ $t('home.trackRecord.signalPrice') }}</span>
                        </v-col>
                        <v-col cols="7" class="text-center">
                          <span class="greyBold--text font-weight-regular">$ {{ formatNumber(item.signalPrice, false) }}</span>
                        </v-col>
                      </v-row>
                      <v-divider class="ma-2"></v-divider>
                      <v-row align="center" justify="center">
                        <v-col cols="5" class="text-center">
                          <span class="font-weight-medium">{{ $t('home.trackRecord.peakPrice') }}</span>
                        </v-col>
                        <v-col cols="7" class="text-center">
                          <span class="greyBold--text font-weight-regular">$ {{ formatNumber(item.price, false) }}</span>
                        </v-col>
                      </v-row>
                      <v-divider class="ma-2"></v-divider>
                      <v-row align="center" justify="center">
                        <v-col cols="5" class="text-center">
                          <span class="font-weight-medium">{{ $t('home.trackRecord.finishDate') }}</span>
                        </v-col>
                        <v-col cols="7" class="text-center">
                          <span class="greyBold--text font-weight-regular">{{ formatDate(item.finishDate) }}</span>
                        </v-col>
                      </v-row>
                      <v-divider class="ma-2"></v-divider>
                      <v-row align="center" justify="center">
                        <v-col cols="5" class="text-center">
                          <span class="font-weight-medium">{{ $t('home.trackRecord.totalDays') }}</span>
                        </v-col>
                        <v-col cols="7" class="text-center">
                          <span class="greyBold--text font-weight-regular">{{ countDaysBetweenDates(formatDate(item.signalDate), formatDate(item.finishDate)) }} {{ $t('home.trackRecord.days') }}</span>
                        </v-col>
                      </v-row>
                      <v-divider class="ma-2"></v-divider>
                      <v-row align="center" justify="center">
                        <v-col cols="5" class="text-center">
                          <span class="font-weight-medium">{{ $t('home.trackRecord.technical') }}</span>
                        </v-col>
                        <v-col cols="7" class="text-center">
                          <div>
                            <v-chip
                              link
                              :color="getCustomIndicatorsColor(item.indicators.technical)"
                            >
                              {{item.indicators.technical}}%
                            </v-chip>
                          </div>
                        </v-col>
                      </v-row>
                      <v-divider class="ma-2"></v-divider>
                      <v-row align="center" justify="center">
                        <v-col cols="5" class="text-center">
                          <span class="font-weight-medium">{{ $t('home.trackRecord.fundamental') }}</span>
                        </v-col>
                        <v-col cols="7" class="text-center">
                          <div>
                            <v-chip
                              link
                              :color="getCustomIndicatorsColor(item.indicators.fundamental)"
                            >
                              {{item.indicators.fundamental}}%
                            </v-chip>
                          </div>
                        </v-col>
                      </v-row>
                      <v-divider class="ma-2"></v-divider>
                      <v-row align="center" justify="center" class="pb-1">
                        <v-col cols="5" class="text-center">
                          <span class="font-weight-medium">{{ $t('home.trackRecord.strength') }}</span>
                        </v-col>
                        <v-col cols="7" class="text-center">
                          <div>
                            <v-chip
                              link
                              :color="getCustomIndicatorsColor(item.indicators.strength)"
                            >
                              {{item.indicators.strength}}%
                            </v-chip>
                          </div>
                        </v-col>
                      </v-row>
                    </div>
                  </td>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
    <BasicModal :modalCode="modalCode"></BasicModal>
  </div>
</template>

<script>
  import { mapState, mapActions } from 'vuex';
  import config from "@/config";
  import mock from './mock';
  import axios from 'axios';

  import ApexChart from 'vue-apexcharts';
  import BasicModal from '@/components/Modal/BasicModal';

  import { UtilsMixin } from '@/mixins/utils';

  export default {
    name: 'Home',

    components: {
      ApexChart, BasicModal
    },

    mixins: [ 
      UtilsMixin 
    ],

    created () {
      this.themeState = this.theme
    },

    mounted() {
      setTimeout(() => {
        this.apexLoading = true
      })
      this.checkDetailButtonInTableColumn(this.$vuetify.breakpoint.name);

      // API calls
      this.getMarketStatus();
      this.getMarketSentiment();
      this.getSignalSummary();
    },

    data() {
      return {
        mock,
        mode: this.$vuetify.theme.dark ? 'dark' : 'light',
        apexLoading: false,
        loadingMarketSentiment: false,
        loadingSignalSummary: false,
        marketSentiment: {
          today: null,
          week: null,
          month: null
        },
        showMarketStatus: false,
        marketStatus: false,
        themeState: false,
        apexDarkTheme: {
          theme: {
            mode: 'dark'
          }
        },
        apexDarkThemeMainChart: {
          theme: {
            mode: 'dark'
          },
          colors: [config.light.warning, config.light.primary, '#30324B'],
        },
        apexLightTheme: {
          theme: {
            mode: 'light'
          }
        },
        apexLightThemeMainChart: {
          theme: {
            mode: 'light'
          },
          colors: [config.light.warning, config.light.primary, '#1c5e5e'],
        },
        showTableRowButtonDetail: false,
        recentSignalHeaders: [
          { text: this.$t('home.recentSignals.ticker'), value: "ticker", sortable: false },
          { text: this.$t('home.recentSignals.rvol'), value: "rvol", align: 'center', sortable: false },
          { text: this.$t('home.recentSignals.status'), value: "status", align: 'center', sortable: false }
        ],
        recentSignalExpanded: [],
        signalSummary: {
          lastSession: {},
          potentialsSummary: {},
          recentSignals: [],
          potentialSignals: [],
          recentTrackRecord: []
        },
        modalCode: '',
        potentialSignalHeaders: [
          { text: this.$t('home.potentialSignalSummary.ticker'), value: "ticker", sortable: false },
          { text: this.$t('home.potentialSignalSummary.currentPrice'), value: "price", sortable: false },
          { text: this.$t('home.potentialSignalSummary.signalPrice'), value: "signalPrice", sortable: false },
        ],
        potentialSignalExpanded: [],
        trackRecordHeaders: [
          { text: this.$t('home.trackRecord.ticker'), value: "ticker", sortable: false },
          { text: this.$t('home.trackRecord.signalDate'), value: "signalDate", align: 'center', sortable: false },
          { text: this.$t('home.trackRecord.gain'), value: "gain", align: 'center', sortable: false },
        ],
        trackRecordExpanded: [],
      };
    },

    computed: {
      ...mapState({
        currentUser: (state) => state.auth.currentUser,
      })
    },

    watch: {
      theme(){
        this.themeState = this.theme
        if (this.themeState) {
          this.$refs.apexArea1.updateOptions(this.apexDarkTheme, false, true)
          this.$refs.apexArea2.updateOptions(this.apexDarkTheme, false, true)
          this.$refs.apexArea3.updateOptions(this.apexDarkTheme, false, true)
          this.$refs.dailyLine.updateOptions(this.apexDarkThemeMainChart, false, true)
          this.$refs.apexBar1.updateOptions(this.apexDarkTheme, false, true)
          this.$refs.apexBar2.updateOptions(this.apexDarkTheme, false, true)
          this.$refs.apexBar3.updateOptions(this.apexDarkTheme, false, true)
        }
        else {
          this.$refs.apexArea1.updateOptions(this.apexLightTheme, false, true)
          this.$refs.apexArea2.updateOptions(this.apexLightTheme, false, true)
          this.$refs.apexArea3.updateOptions(this.apexLightTheme, false, true)
          this.$refs.dailyLine.updateOptions(this.apexLightThemeMainChart, false, true)
          this.$refs.apexBar1.updateOptions(this.apexLightTheme, false, true)
          this.$refs.apexBar2.updateOptions(this.apexLightTheme, false, true)
          this.$refs.apexBar3.updateOptions(this.apexLightTheme, false, true)
        }
      },
      '$vuetify.breakpoint.name'(newVal) {
        this.checkDetailButtonInTableColumn(newVal);
      }
    },
    
    methods: {
      // ---- Datos cargados del store ----
      ...mapActions('modal', ['showModal']),
      // ---- FUNCIONES UTILIZADAS POR LOS COMPONENTES EN PANTALLA  ----
      closeMarketStatusChip () {
        this.showMarketStatus = false;
      },
      getLastSessionPercentage() {
        if (this.signalSummary.lastSession && this.signalSummary.lastSession.breakout && this.signalSummary.lastSession.breakout > 0) {
          return Math.round((this.signalSummary.lastSession.above / this.signalSummary.lastSession.breakout) * 100 * 10) / 10;
        }
        return 0;
      },
      getTotalForPotentialSignalSummary() {
        if (this.signalSummary.potentialsSummary) {
          return [this.signalSummary.potentialsSummary.potential, this.signalSummary.potentialsSummary.likely, this.signalSummary.potentialsSummary.eventual];
        }
        return [];
      },
      checkDetailButtonInTableColumn(size) {
        // Mostrar botón de detalle en las tablas cuando es una pantalla mediana o mayor
        if (size === 'sm' || size === 'md' || size === 'lg' || size === 'xl') {
          if (!this.recentSignalHeaders.some(header => header.value === 'data-table-expand')) {
            this.recentSignalHeaders.push({ text: '', value: 'data-table-expand' });
            this.potentialSignalHeaders.push({ text: '', value: 'data-table-expand' });
            this.trackRecordHeaders.push({ text: '', value: 'data-table-expand' });
            this.showTableRowButtonDetail = true;
          }
        } else {
          this.recentSignalHeaders = this.recentSignalHeaders.filter(header => header.value !== 'data-table-expand');
          this.potentialSignalHeaders = this.potentialSignalHeaders.filter(header => header.value !== 'data-table-expand');
          this.trackRecordHeaders = this.trackRecordHeaders.filter(header => header.value !== 'data-table-expand');
          this.showTableRowButtonDetail = false;
        }
      },
      pushSignalDetailPage(id) {
        this.$router.push({ name: 'SignalDetail', params: { id: id } });
      },
      pushLastSessionActiveSignalsPage() {
        this.$router.push({ name: 'ActiveSignals', params: { lastSessionDate: this.signalSummary.lastSession.day } })
      },
      pushPotentialSignalsPage() {
        this.$router.push({ name: 'PotentialSignals' })
      },
      showErrorModal() {
        this.modalCode = 'apiErrorModal';
        this.showDefaultErrorModal(this.modalCode);
      },
      // ---- PETICIONES AL BACKEND ----
      // Consultar el estado del mercado (abierto/cerrado)
      async getMarketStatus() {
        this.showMarketStatus = false;
        try {
          const response = await axios.get('/market/getMarketStatus');
          this.marketStatus = response.data;
        } catch (e) {
          this.showErrorModal();
        } finally {
          this.showMarketStatus = true;
        }
      },
      // Consultar el sentimiento del mercado
      async getMarketSentiment() {
        this.loadingMarketSentiment = true;
        try {
          const response = await axios.get('/market/getMarketSentimentSummary');
          this.marketSentiment = response.data;
        } catch (e) {
          this.showErrorModal();
        } finally {
          this.loadingMarketSentiment = false;
        }
      },
      // Consultar los datos resumen de señales
      async getSignalSummary() {
        this.loadingSignalSummary = true;
        try {
          const response = await axios.get('/signal/getSignalSummary');
          this.signalSummary = response.data;
        } catch (e) {
          this.showErrorModal();
        } finally {
          this.loadingSignalSummary = false;
        }
      },
    }
  };
</script>